import React, { useEffect, useState } from 'react';
import { Button, LocaleText, Table } from 'ui';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import { config } from 'config/config';
import { Checkbox, Divider, message, Popover } from 'antd';
import generalHelpers from 'utils/generalHelpers';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import coursesApiClient from 'utils/coursesApiClient';
import FileDownload from 'js-file-download';

const defaultFields: string[] = [
    'date',
    'start',
    'end',
    'sessionLength',
    'defaultTitle',
    'title',
    'lectors',
    'lectorsDepartments',
    'attendees',
];

const cacheKey = 'sessionColumns_CUSTOMER_SESSION';

// Saved column selection; missing or empty means the user never saved one,
// so every column is shown by default.
const readCachedColumns = (): string[] | null => {
    const cached = localStorage.getItem(cacheKey);

    return cached ? cached.split(',') : null;
};

const CustomerSessionReport: React.FC<any> = ({ filter, load, setLoad, form }) => {
    const [reload, setReload] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [selectedColumns, setSelectedColumns] = useState<CheckboxValueType[]>(
        () => readCachedColumns() ?? defaultFields,
    );
    const [openPopover, setOpenPopover] = useState(false);
    const [displayColumns, setDisplayColumns] = useState<any>(() => readCachedColumns() ?? defaultFields);

    const intl = useIntl();

    useEffect(() => {
        if (filter !== null && load) {
            filterSession();
            setLoad(false);
        }
    }, [load]);

    const filterSession = () => {
        setReload(true);
    };

    const columns = [
        {
            title: intl.formatMessage({ id: 'general.date' }),
            dataIndex: 'date',
            key: 'date',
            sorter: true,
            render: (date: string) => moment(date).format(config.defaultDateFormat),
        },
        {
            title: intl.formatMessage({ id: 'general.start' }),
            dataIndex: 'start',
            key: 'start',
            sorter: true,
            render: (date: string) => date.substring(0, date.length - 3),
        },
        {
            title: intl.formatMessage({ id: 'general.end' }),
            dataIndex: 'end',
            key: 'end',
            sorter: true,
            render: (date: string) => date.substring(0, date.length - 3),
        },
        {
            title: intl.formatMessage({ id: 'general.session_length' }),
            dataIndex: 'sessionLength',
            key: 'sessionLength',
            sorter: true,
            render: (minutes: number) => generalHelpers.toHoursAndMinutes(minutes),
        },
        {
            title: intl.formatMessage({ id: 'general.lecture_title' }),
            dataIndex: 'defaultTitle',
            key: 'defaultTitle',
            sorter: true,   
            render: (defaultTitle: any, record: any) => <LocaleText text={record.lectureTranslations} defaultText={defaultTitle} />
        },
        {
            title: intl.formatMessage({ id: 'general.session_title' }),
            dataIndex: 'title',
            key: 'title',
            sorter: true,
        },
        {
            title: intl.formatMessage({ id: 'general.lectors' }),
            dataIndex: 'lectors',
            key: 'lectors',
        },
        {
            title: intl.formatMessage({ id: 'general.departments' }),
            dataIndex: 'lectorsDepartments',
            key: 'lectorsDepartments',
        },
        {
            title: intl.formatMessage({ id: 'general.attendees' }),
            dataIndex: 'attendees',
            key: 'attendees',
            sorter: true,
        },
    ];

    const content = (
        <div>
            <Checkbox.Group
                value={selectedColumns}
                onChange={(checkboxGroupColumns: CheckboxValueType[]) =>
                    setSelectedColumns(checkboxGroupColumns)
                }
            >
                {Object.values(columns).map((el: any) => {
                    const excludeList: any = [];

                    if (excludeList.includes(el.title)) {
                        return;
                    }

                    return (
                        <div key={el.dataIndex} className={'flex p-1 gap-5'}>
                            <Checkbox key={el.dataIndex} value={el.dataIndex} />
                            {el.title}
                        </div>
                    );
                })}
            </Checkbox.Group>
            <Divider plain className={'m-2'} />
            <div className={'flex justify-between p-1'}>
                <Button type={'link'} onClick={() => handleCancel()} message='general.cancel' />
                <Button type={'primary'} onClick={() => handleColumnSubmit()} message='general.select' />
            </div>
        </div>
    );

    const handleColumnSubmit = () => {
        setDisplayColumns(selectedColumns);
        localStorage.setItem(cacheKey, selectedColumns.toString());
        setOpenPopover(false);
    };

    const handleCancel = () => {
        setSelectedColumns(readCachedColumns() ?? defaultFields);
        setOpenPopover(false);
    };

    const exportData = async () => {
        const values = form.getFieldsValue();
        const query = generalHelpers.filterParser(values).replace('&', '?');

        setExportLoading(true);
        try {
            const url = '/api/v1/courses/reports/export/SESSIONS' + query;
            const response = await coursesApiClient.request(url, [], 'GET', true, true);

            if (response.fileName) {
                FileDownload(response, response.fileName);
            } else {
                message.error(intl.formatMessage({ id: 'error.data_load' }));
            }
        } catch (error) {
            message.error(intl.formatMessage({ id: 'error.data_load' }));
        } finally {
            setExportLoading(false);
        }
    };

    return (
        <div className={'reports-element'}>
            <div className={'flex justify-between'}>
                <Button loading={exportLoading} type={'outline'} onClick={() => exportData()} message='general.export' />
                <Popover
                    open={openPopover}
                    placement="bottomRight"
                    title={intl.formatMessage({ id: 'general.columns' })}
                    content={content}
                    trigger="click"
                >
                    <Button type={'outline'} onClick={() => setOpenPopover(!openPopover)} message=' ' icon='fa-solid fa-table-columns' />
                </Popover>
            </div>
            <Table
                columns={columns.filter((el: any) => displayColumns.includes(el.dataIndex))}
                url={'/api/v1/courses/reports/customer-session'}
                reload={reload}
                setReload={setReload}
                rowKey={'id'}
                filter={filter}
                scroll={{ x: true }}
                rowSelection={false}
                fetchOnLoad={false}
                hasCustomFields={false}
                showTotalResults={true}
            />
        </div>
    );
};

export default CustomerSessionReport;
